import React, { useCallback, useEffect, useState } from "react";
import { X, ArrowUpRight, ArrowDownRight, Sparkles, RefreshCw, Clock, Layers, TrendingUp, TrendingDown, Info, Lightbulb, Share2, Users } from "lucide-react";
import ShareCard from "./ShareCard";
import { fmtLocal } from "../utils/time";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const fmtTime = (s) => fmtLocal(s);
const fmtDur = (sec) => {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  if (d) return `${d}d ${h}h ${m}m`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};

const Field = ({ label, value, tone }) => (
  <div className="ws-price-cell p-3">
    <div className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--ws-muted)]">{label}</div>
    <div className={`ws-heading text-[14px] font-bold tabular-nums mt-0.5 ${tone === "up" ? "ws-up" : tone === "down" ? "ws-down" : "text-white"}`}>{value}</div>
  </div>
);

const ReviewBlock = ({ label, text }) => (
  text ? (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--ws-cyan)] font-semibold mb-1">{label}</div>
      <p className="text-[12px] text-white leading-relaxed">{text}</p>
    </div>
  ) : null
);

const TradeDetail = ({ positionId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [review, setReview] = useState(null);
  const [marketCtx, setMarketCtx] = useState(true);
  const [loadingD, setLoadingD] = useState(true);
  const [loadingR, setLoadingR] = useState(false);
  const [reviewNote, setReviewNote] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [sharingCommunity, setSharingCommunity] = useState(false);

  const shareToCommunity = useCallback(async () => {
    if (!detail) return;
    setSharingCommunity(true);
    try {
      const res = await fetch(`${API}/community/posts`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: `Closed ${detail.symbol || "trade"} ${detail.direction || ""} for ${detail.net_pnl >= 0 ? "+" : ""}$${Math.abs(Number(detail.net_pnl) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}.`,
          trade: { symbol: detail.symbol, side: detail.direction, pnl: detail.net_pnl },
        }),
      });
      if (!res.ok) throw new Error();
      toast.success("Shared to Community!");
    } catch { toast.error("Could not share to Community."); }
    setSharingCommunity(false);
  }, [detail]);

  const loadDetail = useCallback(async () => {
    setLoadingD(true);
    try {
      const res = await fetch(`${API}/mt5/position/${encodeURIComponent(positionId)}`, { credentials: "include" });
      setDetail(res.ok ? await res.json() : null);
    } catch { setDetail(null); }
    setLoadingD(false);
  }, [positionId]);

  const loadReview = useCallback(async () => {
    setLoadingR(true); setReviewNote(null);
    try {
      const res = await fetch(`${API}/mt5/trade-review`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ position_id: positionId }),
      });
      if (res.ok) {
        const d = await res.json();
        setReview(d.review); setMarketCtx(d.market_context_available);
        setDisclaimer(d.disclaimer || ""); setReviewNote(d.note || null);
      }
    } catch { setReviewNote("Couldn't generate the review — tap Refresh to retry."); }
    setLoadingR(false);
  }, [positionId]);

  useEffect(() => { setDetail(null); setReview(null); loadDetail(); loadReview(); }, [positionId]); // eslint-disable-line

  const buy = detail?.direction === "BUY";

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" data-testid="trade-detail-drawer">
      <div className="absolute inset-0" style={{ background: "rgba(3,7,18,0.6)", backdropFilter: "blur(3px)" }} onClick={onClose} />
      <div className="relative w-full max-w-[460px] h-full flex flex-col ws-panel ws-wsview-drawer" style={{ borderRadius: 0, borderRight: "none", background: "var(--ws-bg-elev, #0a1120)" }}>
        {/* Top bar (not exported) */}
        <div className="px-5 py-3 border-b border-[color:var(--ws-border)] shrink-0 flex items-center justify-between ws-export-hide">
          <span className="text-[11px] tracking-[0.16em] text-[color:var(--ws-muted)] uppercase">Trade Detail</span>
          <div className="flex items-center gap-1.5">
            {detail && (
              <button onClick={() => setShareOpen(true)} className="ws-btn-primary px-3 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5" title="Create a shareable result card" data-testid="open-share-card"><Share2 className="w-3.5 h-3.5" /> Share Card</button>
            )}
            {detail && (
              <button onClick={shareToCommunity} disabled={sharingCommunity} className="ws-btn-ghost px-3 py-1.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5" title="Post this trade to the Community feed" data-testid="share-to-community"><Users className="w-3.5 h-3.5" /> {sharingCommunity ? "Sharing…" : "Community"}</button>
            )}
            <button onClick={onClose} className="ws-btn-ghost p-2 rounded-lg" data-testid="trade-detail-close"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Exportable title */}
          {detail && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${buy ? "bg-[rgba(52,211,153,0.12)]" : "bg-[rgba(248,113,113,0.12)]"}`}>
                {buy ? <ArrowUpRight className="w-3.5 h-3.5 ws-up" /> : <ArrowDownRight className="w-3.5 h-3.5 ws-down" />}
              </div>
              <h3 className="ws-heading text-[18px] font-bold ws-title-gradient leading-tight">{detail.symbol || "Trade"}</h3>
              {detail.direction && <span className={`text-[11px] font-semibold ${buy ? "ws-up" : "ws-down"}`}>{detail.direction}</span>}
              {detail.result && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${detail.result === "Win" ? "ws-up" : detail.result === "Loss" ? "ws-down" : "text-[color:var(--ws-muted)]"}`} style={{ background: "rgba(120,140,180,0.12)" }} data-testid="trade-result">{detail.result}</span>
              )}
              <span className="text-[11px] text-[color:var(--ws-muted)] w-full">Position #{positionId}{detail.partial ? " · partial close" : ""} · Wealth Secret</span>
            </div>
          )}
          {loadingD ? (
            <div className="text-[13px] text-[color:var(--ws-muted)] py-8 text-center">Loading trade breakdown…</div>
          ) : !detail ? (
            <div className="text-[13px] text-[color:var(--ws-muted)] py-8 text-center">Trade not found.</div>
          ) : (
            <></>
          )}
          {detail && (
            <>
              {/* Net P&L hero */}
              <div className="ws-price-cell p-4 text-center">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--ws-muted)]">Net Realized P&amp;L</div>
                <div className={`ws-heading text-[30px] font-bold tabular-nums ${detail.net_pnl >= 0 ? "ws-up" : "ws-down"}`} data-testid="trade-net-pnl">{money(detail.net_pnl)}</div>
              </div>

              {/* Core fields */}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Entry Price" value={detail.entry_price ?? "—"} />
                <Field label="Exit Price" value={detail.exit_price ?? "—"} />
                <Field label="Entry Time" value={fmtTime(detail.entry_time)} />
                <Field label="Exit Time" value={fmtTime(detail.exit_time)} />
                <Field label="Volume" value={detail.volume != null ? `${detail.volume} lot` : "—"} />
                <Field label="Duration" value={fmtDur(detail.duration_seconds)} />
              </div>

              {/* P&L breakdown */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--ws-cyan)] font-semibold mb-2">P&amp;L Breakdown</div>
                <div className="ws-price-cell divide-y divide-[color:var(--ws-border)]" data-testid="trade-pnl-breakdown">
                  {[["Gross P&L", detail.gross_pnl], ["Commission", detail.commission], ["Swap", detail.swap], ["Fees", detail.fee]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between px-3 py-2 text-[12px]">
                      <span className="text-[color:var(--ws-muted)]">{k}</span>
                      <span className={`font-semibold tabular-nums ${v >= 0 ? "text-white" : "ws-down"}`}>{money(v)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-3 py-2 text-[12px]">
                    <span className="text-white font-semibold">Net Realized</span>
                    <span className={`font-bold tabular-nums ${detail.net_pnl >= 0 ? "ws-up" : "ws-down"}`}>{money(detail.net_pnl)}</span>
                  </div>
                </div>
              </div>

              {/* Fills */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--ws-cyan)] font-semibold mb-2 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> All Fills ({detail.fill_count})</div>
                <div className="space-y-1.5" data-testid="trade-fills">
                  {detail.fills.map((f, i) => (
                    <div key={i} className="ws-price-cell px-3 py-2 flex items-center justify-between text-[11px]">
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded font-semibold ${f.entry_type === "IN" ? "bg-[rgba(56,189,248,0.16)] text-[color:var(--ws-cyan)]" : "bg-[rgba(251,191,36,0.16)] text-[#fbbf24]"}`}>{f.entry_type}</span>
                        <span className="text-white tabular-nums">{f.volume} @ {f.price}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`tabular-nums ${f.profit >= 0 ? "text-[color:var(--ws-muted)]" : "ws-down"}`}>{money(f.profit)}</span>
                        <span className="text-[color:var(--ws-muted)]">#{f.ticket}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Review */}
              <div className="pt-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-[color:var(--ws-cyan)]" />
                    <span className="ws-heading text-[14px] font-semibold">WS AI Review</span>
                  </div>
                  <button onClick={loadReview} disabled={loadingR} className="ws-btn-ghost px-2.5 py-1.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1.5" data-testid="trade-review-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingR ? "animate-spin" : ""}`} /> {loadingR ? "Reviewing" : "Refresh"}
                  </button>
                </div>

                {!marketCtx && (
                  <div className="text-[11px] text-[#fbbf24] flex items-start gap-1.5 mb-2"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Live chart/market context isn't available for this instrument — the review is based on your trade's own numbers.</div>
                )}

                {loadingR && !review ? (
                  <div className="text-[13px] text-[color:var(--ws-muted)] py-4 text-center">Reviewing this trade…</div>
                ) : review ? (
                  <div className="space-y-3" data-testid="trade-ai-review">
                    <ReviewBlock label="What happened" text={review.summary} />
                    <div className="grid gap-3">
                      <div className="ws-price-cell p-3" style={{ borderLeft: "2px solid var(--ws-cyan)" }}><ReviewBlock label="Entry quality" text={review.entry_quality} /></div>
                      <div className="ws-price-cell p-3" style={{ borderLeft: "2px solid #fbbf24" }}><ReviewBlock label="Exit quality" text={review.exit_quality} /></div>
                    </div>
                    <ReviewBlock label="Risk / Reward" text={review.risk_reward} />
                    <ReviewBlock label="Holding duration" text={review.duration} />
                    <ReviewBlock label="Outcome" text={review.outcome} />
                    <div className="ws-price-cell p-3 flex gap-2" style={{ borderLeft: `2px solid ${detail.result === "Win" ? "var(--ws-cyan)" : "#f87171"}` }}>
                      {detail.result === "Win" ? <TrendingUp className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--ws-cyan)" }} /> : <TrendingDown className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#f87171" }} />}
                      <div><ReviewBlock label={detail.result === "Win" ? "Key strength" : "Likely mistake"} text={review.mistake_or_strength} /></div>
                    </div>
                    {review.lessons?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--ws-cyan)] font-semibold mb-2 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" /> Actionable Lessons</div>
                        <div className="space-y-1.5">
                          {review.lessons.map((l, i) => (
                            <div key={i} className="ws-price-cell p-2.5 flex gap-2 text-[12px] text-white leading-snug"><span className="text-[color:var(--ws-cyan)] shrink-0">{i + 1}.</span><span>{l}</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-[13px] text-[color:var(--ws-muted)] py-4 text-center">{reviewNote || "No review available."}</div>
                )}

                {disclaimer && <p className="text-[10px] text-[color:var(--ws-muted)] leading-snug italic border-t border-[color:var(--ws-border)] pt-3 mt-3" data-testid="trade-review-disclaimer">{disclaimer}</p>}
              </div>
            </>
          )}
        </div>
      </div>

      {shareOpen && detail && <ShareCard detail={{ ...detail, position_id: positionId }} onClose={() => setShareOpen(false)} />}
    </div>
  );
};

export default TradeDetail;
